/*
 * ParLinx PDP — variant state machine, model toggle, color bubbles, size
 * ruler, snap carousel, sticky buy bar. No dependencies.
 *
 * All data comes from the section's <script type="application/json"
 * data-plx-pdp-config> (both models inlined), so every selection change is
 * resolved locally with zero network requests. Add-to-cart dispatches
 * `plx:cart:add`, which plx-cart.js owns.
 */
(function () {
  var reduce =
    window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Gallery shelf parallax: how hard the band lags the page scroll.
  var SHELF_PARALLAX = 0.06; // px shift per px of viewport-center offset
  var SHELF_SHIFT_MAX = 24; // must stay under the band's inset in the CSS
  // Size ruler ramp: the centered size renders big and bright, neighbors
  // step down (same continuous-distance treatment as the tour tech band).
  // Desktop magnifier lens over the active PDP slide
  var LENS_SIZE = 190; // px, matches the CSS circle
  var LENS_ZOOM = 2.2; // magnification vs the displayed slide size
  var RAIL_MIN_REM = 0.95;
  var RAIL_MAX_REM = 1.7;
  var RAIL_FALLOFF = 1.1; // gaussian sigma, in tick units

  /* ------------------------------------------------------------------ */
  /* Main P-1 PDP                                                        */
  /* ------------------------------------------------------------------ */
  function initPdp(root) {
    if (root.dataset.plxInited) return;
    root.dataset.plxInited = "1";

    var configEl = root.querySelector("[data-plx-pdp-config]");
    if (!configEl) return;
    var config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      return;
    }

    var refs = {
      track: root.querySelector("[data-plx-track]"),
      dots: root.querySelector("[data-plx-dots]"),
      thumbs: root.querySelector("[data-plx-thumbs]"),
      prev: root.querySelector("[data-plx-prev]"),
      next: root.querySelector("[data-plx-next]"),
      title: root.querySelector("[data-plx-title]"),
      price: root.querySelector("[data-plx-price]"),
      switchEl: root.querySelector("[data-plx-switch]"),
      modelLabel: root.querySelector("[data-plx-model-label]"),
      fitNote: root.querySelector("[data-plx-fit-note]"),
      colorLabel: root.querySelector("[data-plx-color-label]"),
      sizeRail: root.querySelector("[data-plx-size-rail]"),
      sizeLabel: root.querySelector("[data-plx-size-label]"),
      variantInput: root.querySelector("[data-plx-variant-id]"),
      atc: root.querySelector("[data-plx-atc]"),
      buybar: root.querySelector("[data-plx-buybar]"),
      buybarThumb: root.querySelector("[data-plx-buybar-thumb]"),
      buybarPrice: root.querySelector("[data-plx-buybar-price]"),
      buybarSummary: root.querySelector("[data-plx-buybar-summary]"),
      buybarAdd: root.querySelector("[data-plx-buybar-add]"),
    };
    if (!refs.track || !refs.variantInput) return;

    /* ---- variant maps: "sizeKey|colorKey" -> variant, per model ---- */
    var maps = {};
    Object.keys(config.models).forEach(function (m) {
      maps[m] = {};
      config.models[m].variants.forEach(function (v) {
        maps[m][v.sizeKey + "|" + v.colorKey] = v;
      });
    });

    function checkedValue(name) {
      var el = root.querySelector('input[name="' + name + '"]:checked');
      return el ? el.value : null;
    }
    function checkedLabel(name) {
      var el = root.querySelector('input[name="' + name + '"]:checked');
      return el ? el.getAttribute("data-label") : "";
    }

    var state = {
      model: config.current,
      colorKey: checkedValue("plx-color"),
      sizeKey: checkedValue("plx-size"),
    };

    function model() {
      return config.models[state.model];
    }
    function variant() {
      return maps[state.model][state.sizeKey + "|" + state.colorKey] || null;
    }
    function colorInfo(key) {
      var colors = model().colors;
      for (var i = 0; i < colors.length; i++) {
        if (colors[i].key === key) return colors[i];
      }
      return null;
    }
    function mediaForColor(key) {
      var color = colorInfo(key);
      var media = color ? color.media : [];
      if (media.length) return media;
      // Degenerate partition: fall back to the union of every group.
      var all = [];
      model().colors.forEach(function (c) {
        c.media.forEach(function (m) {
          all.push(m);
        });
      });
      return all;
    }

    /* ---------------- gallery (coverflow) ---------------- */
    // The enlarged active image sits center; the rest of the set flanks it
    // on both sides at descending sizes, wrapping so the carousel rotates
    // either way from the start. Slide transforms are set from each
    // slide's signed circular offset to the active index.
    var activeSlide = 0;

    function fillTemplate(tpl, i, n) {
      return tpl.replace("__i__", String(i)).replace("__n__", String(n));
    }

    function slideElements() {
      return Array.prototype.slice.call(refs.track.children);
    }

    function layoutSlides() {
      var slides = slideElements();
      var n = slides.length;
      // Flanking offsets are fitted so each slide's own edge (with its
      // border-radius) stays just inside the track's overflow clip; a flat
      // mid-image cut at the track edge reads as a squared-off corner.
      var tw = refs.track.clientWidth;
      var sw = slides.length ? slides[0].offsetWidth : 0;
      var fit = function (scale, inset) {
        return sw ? ((tw / 2 - (sw * scale) / 2 - inset) / sw) * 100 : 0;
      };
      var x1 = fit(0.7, 6);
      slides.forEach(function (slide, i) {
        var rel = ((i - activeSlide) % n + n) % n;
        if (rel > n / 2) rel -= n;
        var abs = Math.abs(rel);
        var sign = rel < 0 ? -1 : 1;
        var x, s, o, z, on = true;
        // Only the center and its two flanks render; deeper offsets are
        // hidden offstage (a third translucent layer ghosted through the
        // 0.8-opacity flanks).
        if (abs === 0) { x = 0; s = 1; o = 1; z = 5; }
        else if (abs === 1) { x = x1 * sign; s = 0.7; o = 0.8; z = 4; }
        else { x = (x1 + 10) * sign; s = 0.5; o = 0; z = 1; on = false; }
        slide.style.transform =
          "translate(calc(-50% + " + x + "%), -50%) scale(" + s + ")";
        slide.style.opacity = String(o);
        slide.style.zIndex = String(z);
        slide.style.pointerEvents = on ? "auto" : "none";
        slide.classList.toggle("is-center", abs === 0);
      });
    }

    function markActive(index) {
      activeSlide = index;
      var mark = function (container) {
        if (!container) return;
        var kids = Array.prototype.slice.call(container.children);
        kids.forEach(function (kid, i) {
          if (i === index) kid.setAttribute("aria-current", "true");
          else kid.removeAttribute("aria-current");
        });
      };
      mark(refs.dots);
      mark(refs.thumbs);
    }

    function goToSlide(index) {
      var n = slideElements().length;
      if (!n) return;
      markActive(((index % n) + n) % n);
      layoutSlides();
    }

    // Arrows step with wrap in both directions.
    function stepSlide(delta) {
      goToSlide(activeSlide + delta);
    }

    // Horizontal swipe rotates; a swipe suppresses the click that follows
    // so releasing over a side slide doesn't also jump to it.
    var swipeX = null;
    var swipedAt = 0;
    refs.track.addEventListener("pointerdown", function (e) {
      swipeX = e.clientX;
    });
    // Slides are <img>s: without this, a mouse drag starts a native image
    // drag, the pointerup never lands, and the swipe dies.
    refs.track.addEventListener("dragstart", function (e) {
      e.preventDefault();
    });
    refs.track.addEventListener("pointerup", function (e) {
      if (swipeX === null) return;
      var dx = e.clientX - swipeX;
      swipeX = null;
      if (Math.abs(dx) > 40) {
        swipedAt = Date.now();
        stepSlide(dx < 0 ? 1 : -1);
      }
    });
    refs.track.addEventListener("click", function (e) {
      if (Date.now() - swipedAt < 400) return;
      var el = e.target;
      while (el && el !== refs.track && !el.classList.contains("plx-pdp__slide")) {
        el = el.parentElement;
      }
      if (!el || el === refs.track) return;
      var idx = slideElements().indexOf(el);
      if (idx >= 0 && idx !== activeSlide) goToSlide(idx);
    });
    if (refs.prev) refs.prev.addEventListener("click", function () { stepSlide(-1); });
    if (refs.next) refs.next.addEventListener("click", function () { stepSlide(1); });

    /* ---------------- magnifier lens (desktop only) ---------------- */
    var lens = root.querySelector("[data-plx-lens]");
    var lensFine = window.matchMedia &&
      window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    if (lens && lensFine) {
      var lensOff = function () { lens.classList.remove("is-on"); };
      refs.track.addEventListener("pointermove", function (e) {
        if (swipeX !== null && Math.abs(e.clientX - swipeX) > 6) {
          // a swipe is in progress: keep the view unobstructed
          lensOff();
          return;
        }
        var slides = slideElements();
        var el = slides[activeSlide];
        var img = el && el.querySelector("img");
        if (!img || !img.currentSrc) { lensOff(); return; }
        var r = img.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right ||
            e.clientY < r.top || e.clientY > r.bottom) {
          lensOff();
          return;
        }
        var track = refs.track.getBoundingClientRect();
        var fx = (e.clientX - r.left) / r.width;
        var fy = (e.clientY - r.top) / r.height;
        lens.style.left = (e.clientX - track.left - LENS_SIZE / 2) + "px";
        lens.style.top = (e.clientY - track.top - LENS_SIZE / 2) + "px";
        lens.style.backgroundImage = "url(" + img.currentSrc + ")";
        lens.style.backgroundSize =
          (r.width * LENS_ZOOM) + "px " + (r.height * LENS_ZOOM) + "px";
        lens.style.backgroundPosition =
          (LENS_SIZE / 2 - fx * r.width * LENS_ZOOM) + "px " +
          (LENS_SIZE / 2 - fy * r.height * LENS_ZOOM) + "px";
        lens.classList.add("is-on");
      });
      refs.track.addEventListener("pointerleave", lensOff);
      refs.track.addEventListener("pointerdown", lensOff);
    }

    function buildIndicators(media) {
      var n = media.length;
      if (refs.dots) {
        refs.dots.innerHTML = "";
        for (var i = 0; i < n; i++) {
          var dot = document.createElement("button");
          dot.type = "button";
          dot.setAttribute("aria-label", fillTemplate(config.strings.goToImage, i + 1, n));
          (function (idx) {
            dot.addEventListener("click", function () {
              goToSlide(idx);
            });
          })(i);
          refs.dots.appendChild(dot);
        }
      }
      if (refs.thumbs) {
        refs.thumbs.innerHTML = "";
        media.forEach(function (m, i) {
          var btn = document.createElement("button");
          btn.type = "button";
          btn.setAttribute("aria-label", fillTemplate(config.strings.goToImage, i + 1, media.length));
          var img = document.createElement("img");
          img.src = m.thumb;
          img.alt = "";
          img.loading = "lazy";
          img.width = 100;
          img.height = 100;
          btn.appendChild(img);
          btn.addEventListener("click", function () {
            goToSlide(i);
          });
          refs.thumbs.appendChild(btn);
        });
      }
      markActive(0);
    }

    function renderSlides(media) {
      refs.track.innerHTML = "";
      media.forEach(function (m, i) {
        var fig = document.createElement("figure");
        fig.className = "plx-pdp__slide";
        fig.setAttribute("data-media-id", String(m.id));
        var img = document.createElement("img");
        img.src = m.src;
        img.alt = m.alt || "";
        img.width = m.w;
        img.height = m.h;
        img.loading = i === 0 ? "eager" : "lazy";
        fig.appendChild(img);
        refs.track.appendChild(fig);
      });
      buildIndicators(media);
      layoutSlides();
    }

    var swapTimer = null;
    function rebuildGallery() {
      var media = mediaForColor(state.colorKey);
      if (reduce) {
        renderSlides(media);
        return;
      }
      refs.track.classList.add("is-swapping");
      window.clearTimeout(swapTimer);
      swapTimer = window.setTimeout(function () {
        renderSlides(media);
        refs.track.classList.remove("is-swapping");
      }, 200);
    }

    /* ---------------- variant application ---------------- */
    function applyVariant() {
      var v = variant();
      var m = model();
      if (v) {
        refs.variantInput.value = String(v.id);
        if (refs.price) refs.price.textContent = v.priceFormatted;
        if (refs.buybarPrice) refs.buybarPrice.textContent = v.priceFormatted;
        if (refs.atc) {
          refs.atc.disabled = !v.available;
          refs.atc.textContent = v.available ? config.strings.addToCart : config.strings.soldOut;
        }
        if (refs.buybarAdd) {
          refs.buybarAdd.disabled = !v.available;
          refs.buybarAdd.textContent = v.available
            ? config.strings.addToCart
            : config.strings.soldOut;
        }
        window.history.replaceState(null, "", m.url + "?variant=" + v.id);
      }
      var color = colorInfo(state.colorKey);
      if (refs.buybarSummary) {
        refs.buybarSummary.textContent =
          (color ? color.label : "") + " · " + (checkedLabel("plx-size") || "");
      }
      if (refs.buybarThumb) {
        var media = mediaForColor(state.colorKey);
        if (media.length) refs.buybarThumb.src = media[0].thumb;
      }
      if (refs.fitNote) refs.fitNote.hidden = !m.fitNote;
      if (refs.title) refs.title.textContent = m.title;
      if (refs.modelLabel) refs.modelLabel.textContent = config.modelLabels[state.model];
      if (refs.switchEl) refs.switchEl.classList.toggle("is-b", state.model === "b");
    }

    function paintAvailability() {
      var ticks = root.querySelectorAll('input[name="plx-size"]');
      ticks.forEach(function (input) {
        var v = maps[state.model][input.value + "|" + state.colorKey];
        var available = !!(v && v.available);
        var tick = input.closest(".plx-ruler__tick");
        var vh = tick && tick.querySelector("[data-plx-availability]");
        if (tick) {
          if (available) tick.removeAttribute("data-unavailable");
          else tick.setAttribute("data-unavailable", "");
        }
        if (vh) vh.textContent = available ? "" : " — " + config.strings.soldOut;
      });
    }

    /* ---------------- size ruler ---------------- */
    /* Set while centerTick's own scroll is in flight. Without it the settle
       handler re-enters: centerTick scrolls -> scroll/scrollend -> onRailSettled
       -> change -> centerTick. On iOS there is no scrollend, so the 120ms
       debounce below fires repeatedly during momentum and that loop competes
       with `scroll-snap-type: x mandatory` re-snapping the rail. */
    var railAutoScroll = 0;

    function centerTick(input, smooth) {
      var tick = input.closest(".plx-ruler__tick");
      if (!tick || !refs.sizeRail) return;
      window.clearTimeout(railAutoScroll);
      railAutoScroll = window.setTimeout(function () {
        railAutoScroll = 0;
      }, smooth && !reduce ? 700 : 100);
      refs.sizeRail.scrollTo({
        left: tick.offsetLeft - (refs.sizeRail.clientWidth - tick.clientWidth) / 2,
        behavior: smooth && !reduce ? "smooth" : "auto",
      });
    }

    function nearestTick() {
      var railCenter = refs.sizeRail.scrollLeft + refs.sizeRail.clientWidth / 2;
      var best = null;
      var bestDist = Infinity;
      var ticks = refs.sizeRail.querySelectorAll(".plx-ruler__tick");
      ticks.forEach(function (tick) {
        var center = tick.offsetLeft + tick.clientWidth / 2;
        var dist = Math.abs(center - railCenter);
        if (dist < bestDist) {
          bestDist = dist;
          best = tick;
        }
      });
      return best;
    }

    function onRailSettled() {
      if (railAutoScroll) return;
      var tick = nearestTick();
      if (!tick) return;
      var input = tick.querySelector('input[name="plx-size"]');
      if (input && !input.checked) {
        input.checked = true;
        // Fire the same path as a tap/keyboard change.
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }

    // Continuous size ramp: the size at the rail center renders big and
    // bright, neighbors step down with distance — the readout line this
    // replaces is gone. Same treatment as the tour tech band (styleBand).
    function styleRail() {
      if (!refs.sizeRail) return;
      var railCenter = refs.sizeRail.scrollLeft + refs.sizeRail.clientWidth / 2;
      var ticks = refs.sizeRail.querySelectorAll(".plx-ruler__tick");
      ticks.forEach(function (tick) {
        var num = tick.querySelector(".plx-ruler__num");
        if (!num) return;
        var unit = Math.max(1, tick.clientWidth);
        var d = (tick.offsetLeft + tick.clientWidth / 2 - railCenter) / unit;
        var w = Math.exp(-(d / RAIL_FALLOFF) * (d / RAIL_FALLOFF));
        num.style.fontSize =
          (RAIL_MIN_REM + (RAIL_MAX_REM - RAIL_MIN_REM) * w).toFixed(3) + "rem";
        num.style.color =
          "rgba(255,255,255," + (0.42 + 0.58 * w).toFixed(3) + ")";
      });
    }
    var railStyleTicking = false;
    function scheduleRailStyle() {
      if (railStyleTicking) return;
      railStyleTicking = true;
      window.requestAnimationFrame(function () {
        railStyleTicking = false;
        styleRail();
      });
    }

    if (refs.sizeRail) {
      refs.sizeRail.addEventListener("scroll", scheduleRailStyle, { passive: true });
      window.addEventListener("resize", scheduleRailStyle, { passive: true });
      if ("onscrollend" in window) {
        refs.sizeRail.addEventListener("scrollend", onRailSettled);
      } else {
        var railDebounce = null;
        refs.sizeRail.addEventListener(
          "scroll",
          function () {
            window.clearTimeout(railDebounce);
            railDebounce = window.setTimeout(onRailSettled, 120);
          },
          { passive: true }
        );
      }

      // Mouse grab-scroll (ticker pattern). Touch stays native via
      // touch-action: pan-x. Mandatory scroll-snap is suspended while
      // grabbing (per-frame scrollLeft writes fight re-snapping), and a
      // capture-phase click guard keeps a drag from toggling a size radio.
      var DRAG_SLOP = 5;
      var railDragging = false;
      var railCaptured = false;
      var railMoved = 0;
      var railLastX = 0;
      var railPointerId = null;

      // Children are text spans; a native text-drag cancels the pointer stream
      // and the pointerup never lands. Both sibling rails guard this.
      refs.sizeRail.addEventListener("dragstart", function (e) {
        e.preventDefault();
      });

      refs.sizeRail.addEventListener("pointerdown", function (e) {
        // Reset before the touch bail-out: a stale railMoved from an earlier
        // mouse drag would make the click guard below swallow the next tap.
        railMoved = 0;
        if (e.pointerType !== "mouse") return;
        railDragging = true;
        railLastX = e.clientX;
        railPointerId = e.pointerId;
        // NB: capture is deliberately NOT taken here. Capturing on pointerdown
        // retargets the following pointerup AND click to the rail, so a plain
        // click on a tick never reached its <label> and no size could be picked
        // with the mouse at all. Capture is taken lazily once this turns out to
        // be a real drag (below), where it earns its keep by keeping the
        // pointer stream alive as the cursor leaves the rail.
      });

      function railMove(e) {
        if (!railDragging || e.pointerId !== railPointerId) return;
        var dx = e.clientX - railLastX;
        railLastX = e.clientX;
        railMoved += Math.abs(dx);
        if (railMoved > DRAG_SLOP) {
          if (!railCaptured) {
            railCaptured = true;
            refs.sizeRail.classList.add("is-grabbing");
            try {
              refs.sizeRail.setPointerCapture(e.pointerId);
            } catch (err) {
              /* synthetic or already-gone pointer: the window listeners below
                 still drive and still end the drag */
            }
          }
          refs.sizeRail.scrollLeft -= dx;
        }
      }

      function railRelease() {
        if (!railDragging) return;
        railDragging = false;
        if (railCaptured && railPointerId !== null) {
          try {
            refs.sizeRail.releasePointerCapture(railPointerId);
          } catch (err) {
            /* already released */
          }
        }
        railCaptured = false;
        railPointerId = null;
        refs.sizeRail.classList.remove("is-grabbing");
        // Snap restores on class removal; settle-select the nearest tick.
        if (railMoved > DRAG_SLOP) onRailSettled();
      }

      // Bound to window, not the rail: the drag has to end even when the
      // release lands somewhere else entirely.
      window.addEventListener("pointermove", railMove);
      window.addEventListener("pointerup", railRelease);
      window.addEventListener("pointercancel", railRelease);
      refs.sizeRail.addEventListener("lostpointercapture", railRelease);

      refs.sizeRail.addEventListener(
        "click",
        function (e) {
          if (railMoved > DRAG_SLOP) {
            e.preventDefault();
            e.stopPropagation();
            railMoved = 0;
          }
        },
        true
      );
    }

    /* ---------------- change handling ---------------- */
    root.addEventListener("change", function (e) {
      var input = e.target;
      if (!(input instanceof HTMLInputElement)) return;

      if (input.name === "plx-size") {
        state.sizeKey = input.value;
        if (refs.sizeLabel) refs.sizeLabel.textContent = input.getAttribute("data-label") || "";
        centerTick(input, true);
        applyVariant();
      } else if (input.name === "plx-color") {
        state.colorKey = input.value;
        if (refs.colorLabel) refs.colorLabel.textContent = input.getAttribute("data-label") || "";
        rebuildGallery();
        paintAvailability();
        applyVariant();
      } else if (input.name === "plx-model") {
        state.model = input.value;
        rebuildGallery();
        paintAvailability();
        applyVariant();
      }
    });

    /* ---------------- add to cart ---------------- */
    function dispatchAdd() {
      var v = variant();
      if (!v || !v.available) return;
      document.dispatchEvent(
        new CustomEvent("plx:cart:add", { detail: { id: v.id, quantity: 1 } })
      );
    }

    var form = refs.variantInput.form;
    if (form) {
      form.addEventListener("submit", function (e) {
        e.preventDefault();
        dispatchAdd();
      });
    }
    if (refs.buybarAdd) {
      refs.buybarAdd.addEventListener("click", dispatchAdd);
    }

    /* ---------------- white shelf parallax ---------------- */
    var shelf = root.querySelector("[data-plx-shelf]");
    if (shelf && !reduce) {
      var shelfTicking = false;
      var shelfShift = function () {
        shelfTicking = false;
        var r = shelf.getBoundingClientRect();
        var delta =
          (window.innerHeight / 2 - (r.top + r.height / 2)) * SHELF_PARALLAX;
        if (delta > SHELF_SHIFT_MAX) delta = SHELF_SHIFT_MAX;
        if (delta < -SHELF_SHIFT_MAX) delta = -SHELF_SHIFT_MAX;
        root.style.setProperty("--shelf-shift", delta.toFixed(1));
      };
      window.addEventListener(
        "scroll",
        function () {
          if (!shelfTicking) {
            shelfTicking = true;
            window.requestAnimationFrame(shelfShift);
          }
        },
        { passive: true }
      );
      window.addEventListener("resize", shelfShift, { passive: true });
      shelfShift();
    }

    /* ---------------- init ---------------- */
    buildIndicators(mediaForColor(state.colorKey));
    layoutSlides();
    window.addEventListener("resize", layoutSlides, { passive: true });
    paintAvailability();
    applyVariant();
    var checkedSize = root.querySelector('input[name="plx-size"]:checked');
    if (checkedSize) centerTick(checkedSize, false);
    scheduleRailStyle();
  }

  /* ------------------------------------------------------------------ */
  /* Generic fallback products                                           */
  /* ------------------------------------------------------------------ */
  function initGeneric(root) {
    if (root.dataset.plxInited) return;
    root.dataset.plxInited = "1";

    var forms = root.querySelectorAll('form[action*="/cart/add"]');
    forms.forEach(function (form) {
      form.addEventListener("submit", function (e) {
        var idInput = form.querySelector('[name="id"]');
        if (!idInput || !idInput.value) return;
        e.preventDefault();
        document.dispatchEvent(
          new CustomEvent("plx:cart:add", {
            detail: { id: parseInt(idInput.value, 10), quantity: 1 },
          })
        );
      });
    });
  }

  function initAll() {
    var pdps = document.querySelectorAll("[data-plx-pdp]");
    for (var i = 0; i < pdps.length; i++) initPdp(pdps[i]);
    var generics = document.querySelectorAll("[data-plx-pdp-generic]");
    for (var j = 0; j < generics.length; j++) initGeneric(generics[j]);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }
  // Shopify theme editor: re-init when the section reloads
  document.addEventListener("shopify:section:load", initAll);
})();
